// Companion/blocks/DashboardBlock.tsx
import { DashboardBlockBody } from './bodies/DashboardBlockBody/DashboardBlockBody';
import type { LegacyChatResult } from '@/types/legacyChat';
import type {
  ChatDashboardBlockPayload,
  ChatDashboardTopic,
} from '@/types/chatBlocks';

/**
 * The performance dashboard, served when a student asks how they are doing.
 *
 * The ported `DashboardBlockBody` reads its numbers out of the legacy
 * `user_data` blob and draws the radar from the store, so this adapter's job is
 * to put the new payload back into that blob. The exam/global toggle keeps
 * working: `get_performance` supplies the scope the block opens on, and the
 * component holds the switch.
 */
const toLegacyTopics = (topics: ChatDashboardTopic[] | undefined) =>
  (topics ?? []).map((topic) => ({
    name: topic.name,
    accuracy: topic.accuracy ?? null,
    // ORA-1101: the readiness ranking's score, when the row has one.
    ...(topic.readiness !== undefined
      ? { readiness: topic.readiness ?? null }
      : {}),
  }));

const toLegacyResult = (
  payload: ChatDashboardBlockPayload
): LegacyChatResult => {
  const { qbank } = payload;
  const weak = toLegacyTopics(payload.weakTopics);
  const strong = toLegacyTopics(payload.strongTopics);
  const isGlobal = payload.view === 'global';

  return {
    user_data: {
      active_exam: payload.examName ?? '',
      qbank: {
        // The component reads an exam-scoped score object when it is on the
        // exam tab and the global aggregates when it is not; the payload
        // describes one scope, so it fills the side it was served for and the
        // other tab falls back to the component's own "?" placeholders.
        exam_score: isGlobal
          ? null
          : {
              correctPercent: qbank?.accuracy ?? undefined,
              totalResponses: qbank?.completed ?? undefined,
              correct: qbank?.correct ?? undefined,
            },
        global_score: isGlobal
          ? {
              correctPercent: qbank?.accuracy ?? undefined,
              totalResponses: qbank?.completed ?? undefined,
              correct: qbank?.correct ?? undefined,
            }
          : null,
        overall_accuracy: isGlobal ? qbank?.accuracy : undefined,
        overall_total: isGlobal ? qbank?.completed : undefined,
        recent_accuracy: qbank?.recentAccuracy ?? undefined,
        recent_total: qbank?.recentTotal ?? undefined,
      },
      weak_topics: isGlobal ? [] : weak,
      strong_topics: isGlobal ? [] : strong,
      weak_topics_global: isGlobal ? weak : [],
      strong_topics_global: isGlobal ? strong : [],
      // ORA-1101: which number ordered the served scope's topics; absent
      // while the server does not rank by readiness.
      ...(payload.metric
        ? {
            [isGlobal ? 'topic_metric_global' : 'topic_metric']: payload.metric,
          }
        : {}),
    },
  };
};

const DashboardBlock = ({
  payload,
}: {
  payload: ChatDashboardBlockPayload;
}) => <DashboardBlockBody data={toLegacyResult(payload)} />;

export default DashboardBlock;
